import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import CollapsibleMultiselect from '../Multiselects/CollapsibleMultiselect';
import CustomModal from '../Modal';
import FormGroup from '../FormGroup';

const SelectGoalTree = ({
    initGoalActions: propInitGoalActions,
    modalIsVisible: propModalIsVisible,
    offers: propOffers,
    goals: propGoals,
    goalActions: propGoalActions,
    blockedIdGoalActions: propBlockedIdGoalActions,
    nesting: propNesting,
    loadingOffers: propLoadingOffers,
    loadingGoals: propLoadingGoals,
    loadingGoalActions: propLoadingGoalActions,
    getListOffers: propGetListOffers,
    getListGoals: propGetListGoals,
    getListGoalActions: propGetListGoalActions,
    onCloseModal: propOnCloseModal,
    onConfirm: propOnConfirm,
    t
}) => {
    const [selectedOffer, setSelectedOffer] = useState({});
    const [selectedGoal, setSelectedGoal] = useState({});
    const [selectedGoalActions, setSelectedGoalActions] = useState([]);
    const [initGoalActions, setInitGoalActions] = useState(propInitGoalActions);

    const getItemKey = useCallback((item) => {
        if (!item || typeof item !== 'object') {
            return item === undefined || item === null ? '' : String(item);
        }

        if (item.id !== undefined && item.id !== null) {
            return String(item.id);
        }

        if (item.value !== undefined && item.value !== null) {
            return String(item.value);
        }

        return item.title ? String(item.title) : '';
    }, []);

    const blockedGoalActionIds = useMemo(() => {
        if (!Array.isArray(propBlockedIdGoalActions)) {
            return new Set();
        }
        return new Set(propBlockedIdGoalActions.map((value) => String(value)));
    }, [propBlockedIdGoalActions]);

    const availableGoals = useMemo(() => {
        if (propNesting !== 'goals') {
            return propGoals;
        }
        if (!blockedGoalActionIds.size) {
            return propGoals;
        }
        return (propGoals || []).filter((goal) => !blockedGoalActionIds.has(getItemKey(goal)));
    }, [propGoals, propNesting, blockedGoalActionIds, getItemKey]);

    const availableGoalActions = useMemo(() => {
        if (propNesting === 'goals') {
            return propGoalActions;
        }
        if (!blockedGoalActionIds.size) {
            return propGoalActions;
        }
        return (propGoalActions || []).filter((action) => !blockedGoalActionIds.has(getItemKey(action)));
    }, [propGoalActions, propNesting, blockedGoalActionIds, getItemKey]);

    const selectedGoalActionsRef = React.useRef(selectedGoalActions);

    useEffect(() => {
        selectedGoalActionsRef.current = selectedGoalActions;
    }, [selectedGoalActions]);

    useEffect(() => {
        !propOffers.length && propGetListOffers();
        initGoalActions && initGoalActionsHandler();
    }, [propOffers.length, propGetListOffers, initGoalActions]);

    useEffect(() => {
        if (initGoalActions && propGoalActions.length ||
            initGoalActions && propGoals.length) {
            const items = propNesting === 'goals' ? propGoals : propGoalActions;
            initGoalActions.onSuccess(items);
            setInitGoalActions(null);
        }
    }, [initGoalActions, propGoalActions.length, propGoals.length, propNesting]);

    const initGoalActionsHandler = useCallback(() => {
        if (propNesting === 'goals') {
            propGetListGoals({ids: initGoalActions.ids});
        } else {
            propGetListGoalActions({ids: initGoalActions.ids});
        }
    }, [propNesting, initGoalActions, propGetListGoals, propGetListGoalActions]);

    const resetSelected = useCallback(() => {
        setSelectedOffer({});
        setSelectedGoal({});
        setSelectedGoalActions([]);
    }, []);

    const disabledGoalActions = useCallback(() => {
        if (propLoadingGoalActions || !selectedGoal.value) {
            return true;
        }

        return !(availableGoalActions && availableGoalActions.length);
    }, [propLoadingGoalActions, selectedGoal.value, availableGoalActions]);

    const disabledGoal = useCallback(() => {
        if (propLoadingGoals || !selectedOffer.value) {
            return true;
        }

        return !(availableGoals && availableGoals.length);
    }, [propLoadingGoals, selectedOffer.value, availableGoals]);

    const onChangeOffer = useCallback((item) => {
        setSelectedOffer(item);
        setSelectedGoalActions([]);
        propGetListGoals({offerIds: item.value});
    }, [propGetListGoals]);

    const onChangeGoal = useCallback((item) => {
        setSelectedGoal(item);
        propGetListGoalActions({goalIds: item.value});
    }, [propGetListGoalActions]);

    const onChangeGoalActions = useCallback((items) => {
        setSelectedGoalActions(items);
    }, []);

    const onCloseModal = useCallback(() => {
        resetSelected();
        propOnCloseModal && propOnCloseModal();
    }, [resetSelected, propOnCloseModal]);

    const onConfirm = useCallback(() => {
        onCloseModal();
        propOnConfirm && propOnConfirm(selectedGoalActionsRef.current);
    }, [onCloseModal, propOnConfirm]);

    const staticNames = t?.selectGoalActions || {};
    const staticActions = t?.actions || {};

    return (
        <CustomModal
            className="add-goals-modal"
            visible={propModalIsVisible}
            onClose={onCloseModal}
            title={staticNames.title}
            confirmName={staticActions.add}
            confirmDisabled={!selectedGoalActions.length}
            onConfirm={onConfirm}
        >
            <FormGroup
                label={staticNames.offer}
            >
                <DropdownList
                    className={'rw-dropdownlist-select'}
                    dataKey="id"
                    textField='title'
                    busy={propLoadingOffers}
                    data={propOffers}
                    value={selectedOffer.title}
                    onChange={onChangeOffer}
                />
            </FormGroup>
            {propNesting === 'goals'
                ? (
                    <FormGroup
                        label={staticNames.goal}
                    >
                        <div className="custom-input">
                            <CollapsibleMultiselect
                                className={'select-with-chevron'}
                                dataKey="value"
                                textField='title'
                                disabled={disabledGoal()}
                                busy={propLoadingGoals}
                                data={availableGoals}
                                value={selectedGoalActions}
                                onChange={onChangeGoalActions}
                                messages={t?.multiselect || {}}
                            />
                        </div>
                    </FormGroup>
                )
                : (
                    <React.Fragment>
                        <FormGroup
                            label={staticNames.goal}
                        >
                            <DropdownList
                                className={'rw-dropdownlist-select'}
                                dataKey="id"
                                textField='title'
                                disabled={propLoadingGoals || !propGoals.length || !selectedOffer.value}
                                busy={propLoadingGoals}
                                data={propGoals}
                                value={selectedGoal.title}
                                onChange={onChangeGoal}
                            />
                        </FormGroup>
                        <FormGroup
                            label={staticNames.goalActions}
                        >
                            <div className="custom-input">
                                <CollapsibleMultiselect
                                    className={'select-with-chevron'}
                                    dataKey="id"
                                    textField='title'
                                    disabled={disabledGoalActions()}
                                    busy={propLoadingGoalActions}
                                    data={availableGoalActions}
                                    value={selectedGoalActions}
                                    onChange={onChangeGoalActions}
                                    messages={t?.multiselect || {}}
                                />
                            </div>
                        </FormGroup>
                    </React.Fragment>
                )
            }
        </CustomModal>
    );
};

SelectGoalTree.propTypes = {
    initGoalActions: PropTypes.shape({
        ids: PropTypes.string,
        onSuccess: PropTypes.func
    }),
    modalIsVisible: PropTypes.bool,
    offers: PropTypes.array,
    goals: PropTypes.array,
    goalActions: PropTypes.array,
    blockedIdGoalActions: PropTypes.array,
    nesting: PropTypes.string,
    loadingOffers: PropTypes.bool,
    loadingGoals: PropTypes.bool,
    loadingGoalActions: PropTypes.bool,
    getListOffers: PropTypes.func,
    getListGoals: PropTypes.func,
    getListGoalActions: PropTypes.func,
    onCloseModal: PropTypes.func,
    onConfirm: PropTypes.func,
    t: PropTypes.object
};

SelectGoalTree.defaultProps = {
    initGoalActions: null,
    modalIsVisible: false,
    offers: [],
    goals: [],
    goalActions: [],
    blockedIdGoalActions: [],
    nesting: '',
    loadingOffers: false,
    loadingGoals: false,
    loadingGoalActions: false,
    getListOffers: () => {},
    getListGoals: () => {},
    getListGoalActions: () => {},
    onCloseModal: () => {},
    onConfirm: () => {}
};

export default withTranslation(SelectGoalTree);
